import React, { useState, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import { ReportPDF } from '../components/items/Dashboard/ReportPDF';
import type { ReportPDFProps } from '../components/items/Dashboard/ReportPDF';
import type { ListingSummary } from '../types/ListingTypes';
import type { OverallStats } from 'types/DocumentManagement';

export interface UseReportsOptions {
  listings: ListingSummary[];
  agentRows: any[];
  overallStats: OverallStats;
  categoriesWithStats: any[];
}

export interface DownloadReportOptions {
  userDetails: any;
}

export const useReports = ({ listings, agentRows, overallStats, categoriesWithStats }: UseReportsOptions) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const downloadPDF = useCallback(
    async ({ userDetails }: DownloadReportOptions) => {
      setIsGenerating(true);
      try {
        const isManager =
          userDetails?.role === 'Manager' ||
          userDetails?.role === 'Admin' ||
          userDetails?.role === 'Super Admin';

        const props: ReportPDFProps = {
          agencyName: userDetails?.agency?.name ?? 'Agency',
          generatedAt: new Date().toLocaleString('en-ZA'),
          userName: userDetails
            ? `${userDetails.fullNames ?? ''} ${userDetails.lastName ?? ''}`.trim()
            : '',
          role: userDetails?.role ?? '',
          isManager,
          overallStats,
          categoriesWithStats: categoriesWithStats as ReportPDFProps['categoriesWithStats'],
          agentRows: isManager ? agentRows : [],
          listings,
        };

        const blob = await pdf(React.createElement(ReportPDF, props) as any).toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `realty-genie-report-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } finally {
        setIsGenerating(false);
      }
    },
    [overallStats, categoriesWithStats, agentRows, listings]
  );

  return { downloadPDF, isGenerating };
};